import React, { useEffect, useState, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import Helmet from '@plone/volto/helpers/Helmet/Helmet';
import { buildAppJsonLd } from 'nsdi-volto-frontend/utils/schemaOrgHelpers';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFilter,
  faChevronUp,
  faChevronDown,
  faLink,
  faCheck,
  faEye,
  faIdCard,
  faTable,
  faGripHorizontal,
  faListAlt,
  faSort,
} from '@fortawesome/free-solid-svg-icons';
import SearchHeader from './components/SearchHeader/SearchHeader';
import SearchScopeToggle, {
  type SearchScopeButton,
} from './components/SearchScopeToggle/SearchScopeToggle';
import FilterSidebar from './components/FilterSidebar/FilterSidebar';
import MetaDataSearchPage from './pages/MetaDataSearchPage/MetaDataSearchPage';
import { resolveBlockImageSrc } from './utils/blockImage';
import type { BlockImageItem } from './utils/blockImage';
import { useClipboardLink } from './hooks/useClipboardLink';
import { useHideSearchPageChrome } from './hooks/useHideSearchPageChrome';
import { useLazyMapBBoxSelector } from './hooks/useLazyMapBBoxSelector';
import { useSearchFields } from './hooks/useSearchFields';
// Use VoyagerSearchContext for Voyager API integration
import {
  VoyagerSearchProvider as SearchProvider,
  useVoyagerSearch as useSearch,
} from './context/VoyagerSearchContext';
import type { FilterItem, SearchField } from './models/search-result';
import {
  DEFAULT_VOYAGER_FACET_FIELDS,
  DEFAULT_VOYAGER_FACET_PARAMS,
  type VoyagerFacetFieldConfig,
  type VoyagerFacetParamConfig,
} from './Services/VoyagerSearchService';
import { logError } from 'nsdi-volto-frontend/utils/logger';
import { environment } from 'nsdi-volto-frontend/config/environments';
import { parseAgolEligibleFormats } from './constants/agolConfig';
import {
  parseFacetValueCategories,
  parseObjectListValue,
  parseOptionalNumber,
} from 'nsdi-volto-frontend/utils/parseUtils';
import './MetaDataSearchPageBlock.scss';

// 2. use that interface on the component
// View type options
type ViewType = 'card' | 'table' | 'grid' | 'summary';

const viewOptions: { value: ViewType; label: string; icon: any }[] = [
  { value: 'card', label: 'Card', icon: faIdCard },
  { value: 'table', label: 'Table', icon: faTable },
  { value: 'grid', label: 'Grid', icon: faGripHorizontal },
  { value: 'summary', label: 'Summary', icon: faListAlt },
];

// Sort options matching Voyager config
type SortField =
  | 'score'
  | 'fd__acquired'
  | 'name'
  | 'created'
  | 'fi__cloud_cover'
  | 'title';

const sortOptions: { value: SortField; label: string }[] = [
  { value: 'score', label: 'Relevance' },
  { value: 'fd__acquired', label: 'Date Acquired' },
  { value: 'name', label: 'Name' },
  { value: 'created', label: 'Created' },
  { value: 'fi__cloud_cover', label: 'Cloud Cover' },
  { value: 'title', label: 'Title' },
];

const SEARCH_SCOPE_ALL = 'all';

// Default search scope buttons used when the block has none configured.
// Admins can override these (label + raw Solr fq) in the block settings.
const DEFAULT_SEARCH_SCOPE_BUTTONS: { label: string; fq: string }[] = [
  {
    label: 'Metadata',
    fq: 'format:("dcat dataset" OR "iso 19115" OR "application/rss+xml")',
  },
];

const slugifyScopeId = (label: string, index: number): string => {
  const slug = label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || `scope-${index}`;
};

// A configured scope button. Its raw fq is parsed into individual filter items
// so concrete field scopes render as removable filter pills (as before). When
// the fq can't be parsed into pills (e.g. a match-all `*:*`), the raw fq is
// applied directly via the query's scopeFq param instead.
interface ScopeButton extends SearchScopeButton {
  filters: FilterItem[];
}

// Parse a raw Solr fq expression into individual filter items so each value
// shows as its own pill. Supports both:
//   field:("a" OR "b" OR "c")
//   (field:"a" OR field:"b" OR field:"c")
const parseFqToFilters = (fq: string): FilterItem[] => {
  const filters: FilterItem[] = [];
  const seen = new Set<string>();

  const addFilter = (sectionId: string, value: string) => {
    const cleanSection = sectionId.trim();
    const cleanValue = value.trim();
    if (!cleanSection || !cleanValue) {
      return;
    }
    const key = `${cleanSection}:|:${cleanValue}`;
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    filters.push({
      sectionId: cleanSection,
      id: cleanValue,
      label: cleanValue,
      isChecked: true,
    });
  };

  // Form: field:( "a" OR "b" ) — skip negated groups like -field:(...)
  const groupRe = /(-?)([\w.]+):\(([^)]*)\)/g;
  let groupMatch: RegExpExecArray | null;
  while ((groupMatch = groupRe.exec(fq)) !== null) {
    if (groupMatch[1] === '-') {
      continue;
    }
    const field = groupMatch[2];
    const valueRe = /"([^"]+)"|'([^']+)'/g;
    let valueMatch: RegExpExecArray | null;
    while ((valueMatch = valueRe.exec(groupMatch[3])) !== null) {
      addFilter(field, valueMatch[1] ?? valueMatch[2] ?? '');
    }
  }

  // Form: field:"value" pairs — skip negated pairs like -field:"value"
  const pairRe = /(-?)([\w.]+):"([^"]+)"/g;
  let pairMatch: RegExpExecArray | null;
  while ((pairMatch = pairRe.exec(fq)) !== null) {
    if (pairMatch[1] === '-') {
      continue;
    }
    addFilter(pairMatch[2], pairMatch[3]);
  }

  return filters;
};

// Interface for card display field configuration
interface CardDisplayField {
  field: string;
  label: string;
}

interface MetaDataSearchPageBlockContentProps {
  data?: MetaDataSearchPageBlockData;
}

export interface MetaDataSearchPageBlockData extends Record<string, unknown> {
  _sidebarFacetConfigInitialized?: boolean;
  detailsPagePath?: string;
  cardBadgeField?: string;
  placeholder?: string;
  showMap?: boolean;
  cardColumns?: number;
  agolEligibleFormats?: string;
  sidebarFacetValueCategoriesCollapsedByDefault?: boolean;
  cardDisplayFields?: unknown;
  voyagerBaseUrl?: string;
  voyagerConfigId?: string;
  sidebarFacetFields?: unknown;
  sidebarFacetParams?: unknown;
  sidebarFacetLimit?: number;
  sidebarFacetMinCount?: number;
  searchScopeLabel?: string;
  searchScopeButtons?: unknown;
  hierarchyTreeBase?: string;
  // Per-page branding. Unset falls back to the site-wide images, so existing
  // pages are unaffected until someone sets one.
  thumbnailImage?: BlockImageItem[];
  headerBanner?: BlockImageItem[];
  footerBanner?: BlockImageItem[];
  accentColor?: string;
  // Consumed by the Open In AGOL component, which lands on a separate branch.
  arcgisUrl?: string;
}

const parseCategoryPath = (value: unknown): string[] | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const parts = value
    .split('>')
    .map((part) => part.trim())
    .filter(Boolean);

  return parts.length ? parts : undefined;
};

const MetaDataSearchPageBlockContent: React.FC<
  MetaDataSearchPageBlockContentProps
> = (props) => {
  // Get block configuration from props
  const { data = {} } = props;
  const detailsPagePath = data.detailsPagePath || '/voyager-details';
  const cardBadgeField = data.cardBadgeField || '';
  const placeholderText = data.placeholder || 'What are you looking for?';
  const showMap = data.showMap !== false; // Default to true
  const cardColumns = data.cardColumns || 4;
  const agolEligibleFormats = parseAgolEligibleFormats(
    data.agolEligibleFormats,
  );
  const sidebarFacetValueCategoriesCollapsedByDefault =
    data.sidebarFacetValueCategoriesCollapsedByDefault !== false;

  // Parse card display fields configuration
  const defaultCardFields: CardDisplayField[] = [
    { field: 'format', label: 'Format' },
    { field: 'fd__acquired', label: 'Acquired' },
    { field: 'fs__provider', label: 'Provider' },
  ];
  const cardDisplayFields = parseObjectListValue<CardDisplayField>(
    data.cardDisplayFields,
    defaultCardFields,
    (item) => {
      if (!item?.field || !item?.label) {
        return null;
      }

      return {
        field: item.field,
        label: item.label,
      };
    },
    logError,
  );

  // Parse configurable search scope buttons (label + raw Solr fq).
  const searchScopeLabel = data.searchScopeLabel ?? 'Search in:';
  const searchScopeButtons = useMemo<ScopeButton[]>(() => {
    const parsed = parseObjectListValue<{ label: string; fq: string }>(
      data.searchScopeButtons,
      DEFAULT_SEARCH_SCOPE_BUTTONS,
      (item) => {
        const label = typeof item?.label === 'string' ? item.label.trim() : '';
        const fq = typeof item?.fq === 'string' ? item.fq.trim() : '';
        if (!label || !fq) {
          return null;
        }
        return { label, fq };
      },
      logError,
    );

    return parsed.map((button, index) => ({
      id: slugifyScopeId(button.label, index),
      label: button.label,
      fq: button.fq,
      filters: parseFqToFilters(button.fq),
    }));
  }, [data.searchScopeButtons]);

  const [showFilters, setShowFilters] = useState(true);
  const [viewType, setViewType] = useState<ViewType>('card');

  const token = useSelector((state: any) => state.userSession?.token);
  const pathname = useSelector(
    (state: any) => state.router?.location?.pathname ?? '',
  );
  const isEditMode = pathname.endsWith('/edit');
  // Per-page, from block data. Previously site-wide via the Data Manager Admin
  // block, which has been removed - each search page now carries its own.
  const accentColor = data.accentColor || '';
  const headerBannerSrc = resolveBlockImageSrc(data.headerBanner);
  const logoSrc = resolveBlockImageSrc(data.thumbnailImage);
  const footerBannerSrc = resolveBlockImageSrc(data.footerBanner);
  const { linkCopied, copyLinkToClipboard } = useClipboardLink();
  useHideSearchPageChrome(isEditMode);

  const [viewDropdownOpen, setViewDropdownOpen] = useState(false);
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false);
  const sortButtonRef = useRef<HTMLButtonElement | null>(null);
  const viewButtonRef = useRef<HTMLButtonElement | null>(null);
  const [sortMenuPos, setSortMenuPos] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const [viewMenuPos, setViewMenuPos] = useState<{
    top: number;
    right: number;
  } | null>(null);
  const MapBBoxSelector = useLazyMapBBoxSelector();
  const { searchFields, handleCheckboxChange, setSearchFieldsFromArray } =
    useSearchFields();
  const {
    activeFilter,
    updateActiveFilter,
    updateSearchFields,
    applySearchParams,
    searchState,
  } = useSearch();

  // Initialize viewType from URL parameters (via searchState)
  useEffect(() => {
    const urlViewType = searchState?.query?.viewType;
    if (
      urlViewType &&
      ['card', 'table', 'grid', 'summary'].includes(urlViewType)
    ) {
      setViewType(urlViewType as ViewType);
    }
  }, [searchState?.query?.viewType]);

  // The active scope is tracked explicitly on the query (searchScope). For
  // scopes that render as pills, we also confirm the pills are still present so
  // manually removing a pill deactivates the button (matching prior UX).
  const activeScopeId = useMemo<string>(() => {
    const currentScope = searchState?.query?.searchScope;
    if (!currentScope || currentScope === SEARCH_SCOPE_ALL) {
      return SEARCH_SCOPE_ALL;
    }
    const button = searchScopeButtons.find((item) => item.id === currentScope);
    if (!button) {
      return SEARCH_SCOPE_ALL;
    }
    if (button.filters.length > 0) {
      const currentFilters = (searchState?.query?.filters ||
        []) as FilterItem[];
      const allPresent = button.filters.every((scopeFilter) =>
        currentFilters.some(
          (filter) =>
            filter.sectionId === scopeFilter.sectionId &&
            filter.id === scopeFilter.id,
        ),
      );
      return allPresent ? button.id : SEARCH_SCOPE_ALL;
    }
    return button.id;
  }, [
    searchState?.query?.searchScope,
    searchState?.query?.filters,
    searchScopeButtons,
  ]);

  const computeMenuPosition = (button: HTMLButtonElement | null) => {
    if (!button || typeof window === 'undefined') {
      return null;
    }
    const rect = button.getBoundingClientRect();
    return {
      top: rect.bottom + 4,
      right: Math.max(window.innerWidth - rect.right, 8),
    };
  };

  useEffect(() => {
    if (!sortDropdownOpen && !viewDropdownOpen) {
      return;
    }
    const updatePositions = () => {
      if (sortDropdownOpen) {
        setSortMenuPos(computeMenuPosition(sortButtonRef.current));
      }
      if (viewDropdownOpen) {
        setViewMenuPos(computeMenuPosition(viewButtonRef.current));
      }
    };
    updatePositions();
    window.addEventListener('scroll', updatePositions, true);
    window.addEventListener('resize', updatePositions);
    return () => {
      window.removeEventListener('scroll', updatePositions, true);
      window.removeEventListener('resize', updatePositions);
    };
  }, [sortDropdownOpen, viewDropdownOpen]);

  const toggleFilters = () => {
    setShowFilters((prev) => !prev);
  };

  const resolveFilterFromResults = (
    sectionId: string,
    filterId: string,
    fallbackLabel: string,
  ): FilterItem => {
    const section = searchState.results?.filterSections?.find(
      (filterSection) => filterSection.id === sectionId,
    );
    const filterMatch = section?.filters?.find(
      (filterItem) => filterItem.id === filterId,
    );

    if (filterMatch) {
      return {
        sectionId,
        label: filterMatch.label,
        id: filterMatch.id,
        isChecked: true,
      };
    }

    return {
      sectionId,
      label: fallbackLabel,
      id: filterId,
      isChecked: true,
    };
  };

  // Sync checkboxes with search state whenever search fields change
  const currentSearchFields = searchState.query?.settings?.searchFields;
  useEffect(() => {
    if (currentSearchFields && currentSearchFields.length > 0) {
      setSearchFieldsFromArray(currentSearchFields);
    }
  }, [currentSearchFields, setSearchFieldsFromArray]);

  // Wrap checkbox change to update context and URL
  const handleCheckboxChangeWithUpdate = (
    field: 'title' | 'abstract' | 'keyword' | 'any',
  ) => {
    // First update the local state
    handleCheckboxChange(field);

    // Calculate the new search fields after the change
    let newSearchFields: SearchField[] = [];
    if (field === 'any') {
      newSearchFields = ['any'];
    } else {
      const updatedFields = { ...searchFields };
      updatedFields[field] = !updatedFields[field];
      updatedFields.any = false;

      if (updatedFields.title) newSearchFields.push('title');
      if (updatedFields.abstract) newSearchFields.push('abstract');
      if (updatedFields.keyword) newSearchFields.push('keyword');

      // If nothing selected, default to 'any'
      if (newSearchFields.length === 0) {
        newSearchFields = ['any'];
      }
    }

    updateSearchFields(newSearchFields);
    applySearchParams({ searchFields: newSearchFields, pageIndex: 0 });
  };

  const handleFilterChange = (filter: string) => {
    updateActiveFilter(filter);

    let filters: FilterItem[] = [];

    if (filter === 'all') {
      filters = [];
    } else if (filter === 'ngda-themes') {
      // For Voyager, we can filter by data location type
      filters = [
        resolveFilterFromResults(
          'grp_data_location_type',
          'Geospatial Data',
          'Geospatial Data',
        ),
      ];
    } else if (filter === 'datasets') {
      // Filter by format type for datasets
      filters = [
        resolveFilterFromResults('format', 'application/pdf', 'PDF Documents'),
      ];
    } else if (filter === 'services') {
      // Filter by geospatial services
      filters = [
        resolveFilterFromResults(
          'format',
          'application/x-arcgis-map-server',
          'ArcGIS Map Server',
        ),
      ];
    } else if (filter === 'cached-services') {
      // Filter by image formats (cached services)
      filters = [resolveFilterFromResults('format', 'image/png', 'PNG Images')];
    } else if (filter === 'reports') {
      // Voyager-specific: Reports and Briefs
      filters = [
        resolveFilterFromResults(
          'grp_data_location_type',
          'Reports and Briefs',
          'Reports and Briefs',
        ),
      ];
    }

    applySearchParams({ filters, pageIndex: 0 });
  };

  const handleSearchScopeChange = (scopeId: string) => {
    const button = searchScopeButtons.find((item) => item.id === scopeId);
    if (!button) {
      return;
    }

    // Buttons are mutually exclusive, so first strip the pill filters that
    // belong to ANY configured scope, keeping the user's other filters.
    const currentFilters = (searchState.query?.filters || []) as FilterItem[];
    const belongsToAnyScope = (filter: FilterItem) =>
      searchScopeButtons.some((scopeButton) =>
        scopeButton.filters.some(
          (scopeFilter) =>
            scopeFilter.sectionId === filter.sectionId &&
            scopeFilter.id === filter.id,
        ),
      );
    const nonScopeFilters = currentFilters.filter(
      (filter) => !belongsToAnyScope(filter),
    );

    // Clicking the active button clears the scope.
    const isActive = scopeId === activeScopeId;
    if (isActive) {
      applySearchParams({
        pageIndex: 0,
        filters: nonScopeFilters,
        searchScope: SEARCH_SCOPE_ALL,
        scopeFq: undefined,
        scopeLabel: undefined,
      });
      return;
    }

    // Concrete field scopes render as removable filter pills. Match-all or
    // otherwise unparseable expressions (e.g. `*:*`) are applied via scopeFq.
    const hasPillFilters = button.filters.length > 0;
    applySearchParams({
      pageIndex: 0,
      filters: hasPillFilters
        ? [...nonScopeFilters, ...button.filters]
        : nonScopeFilters,
      searchScope: button.id,
      scopeFq: hasPillFilters ? undefined : button.fq,
      scopeLabel: button.label,
    });
  };

  return (
    <div
      className={
        isEditMode
          ? 'metadata-search-page-edit-wrapper'
          : 'search-page-fullwidth-wrapper'
      }
      style={
        {
          '--site-accent-color': accentColor || '#217821',
        } as React.CSSProperties
      }
    >
      <div className="metadata-search-page-layout">
        <div className="top-section">
          {/* Banner as hero background - omitted when the page sets none */}
          {headerBannerSrc && (
            <img
              src={headerBannerSrc}
              alt=""
              className="top-section__hero-bg"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
          )}
          <div className="top-section__content">
            <div className="site-search-row">
              {logoSrc && (
                <img
                  src={logoSrc}
                  alt=""
                  className="site-search-row__logo"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                  }}
                />
              )}
              <SearchHeader
                searchFields={searchFields}
                activeFilter={activeFilter}
                placeholderText={placeholderText}
                onCheckboxChange={handleCheckboxChangeWithUpdate}
                onFilterChange={handleFilterChange}
              />
            </div>
            <div className="search-actions-bar">
              <div className="actions-left">
                <button
                  className="action-btn filters-btn"
                  onClick={toggleFilters}
                  title={showFilters ? 'Hide Filters' : 'Show Filters'}
                  aria-label={showFilters ? 'Hide Filters' : 'Show Filters'}
                >
                  <FontAwesomeIcon icon={faFilter} />
                  <span className="btn-text">Filters</span>
                  <FontAwesomeIcon
                    icon={showFilters ? faChevronUp : faChevronDown}
                    className="btn-chevron"
                  />
                </button>
                {searchState?.results?.count !== undefined && (
                  <div className="results-count">
                    <span className="results-label">Results</span>
                    <span className="results-badge">
                      {searchState.results.count.toLocaleString()}
                    </span>
                  </div>
                )}
                <SearchScopeToggle
                  label={searchScopeLabel}
                  buttons={searchScopeButtons}
                  activeId={activeScopeId}
                  onSelect={handleSearchScopeChange}
                />
              </div>
              <div className="actions-right">
                <button
                  className={`action-btn link-btn ${linkCopied ? 'copied' : ''}`}
                  onClick={copyLinkToClipboard}
                  title={linkCopied ? 'Link Copied!' : 'Get Link'}
                  aria-label={linkCopied ? 'Link Copied!' : 'Get Link'}
                >
                  <FontAwesomeIcon icon={linkCopied ? faCheck : faLink} />
                  <span className="btn-text">
                    {linkCopied ? 'Copied!' : 'Get Link'}
                  </span>
                </button>
                <div className="sort-dropdown-container">
                  <button
                    ref={sortButtonRef}
                    className="action-btn sort-btn"
                    onClick={() => {
                      const next = !sortDropdownOpen;
                      if (next) {
                        setSortMenuPos(
                          computeMenuPosition(sortButtonRef.current),
                        );
                        setViewDropdownOpen(false);
                      }
                      setSortDropdownOpen(next);
                    }}
                    title="Sort"
                    aria-label="Sort"
                    aria-expanded={sortDropdownOpen}
                  >
                    <FontAwesomeIcon icon={faSort} />
                    <span className="btn-text">Sort</span>
                    <FontAwesomeIcon
                      icon={sortDropdownOpen ? faChevronUp : faChevronDown}
                      className="btn-chevron"
                    />
                  </button>
                  {sortDropdownOpen &&
                    typeof document !== 'undefined' &&
                    createPortal(
                      <div
                        className="sort-dropdown-menu sort-dropdown-menu--portal"
                        style={{
                          position: 'fixed',
                          top: sortMenuPos?.top ?? 0,
                          right: sortMenuPos?.right ?? 8,
                        }}
                      >
                        <div className="sort-direction-section">
                          <button
                            className={`sort-direction-btn ${(searchState.query?.sortOrder || 'desc') === 'asc' ? 'active' : ''}`}
                            onClick={() => {
                              applySearchParams({
                                sortOrder: 'asc',
                                pageIndex: 0,
                              });
                            }}
                          >
                            Ascending
                          </button>
                          <button
                            className={`sort-direction-btn ${(searchState.query?.sortOrder || 'desc') === 'desc' ? 'active' : ''}`}
                            onClick={() => {
                              applySearchParams({
                                sortOrder: 'desc',
                                pageIndex: 0,
                              });
                            }}
                          >
                            Descending
                          </button>
                        </div>
                        <div className="sort-field-section">
                          {sortOptions.map((option) => (
                            <button
                              key={option.value}
                              className={`sort-field-item ${(searchState.query?.sortBy || 'score') === option.value ? 'active' : ''}`}
                              onClick={() => {
                                applySearchParams({
                                  sortBy: option.value,
                                  pageIndex: 0,
                                });
                                setSortDropdownOpen(false);
                              }}
                            >
                              {option.label}
                              {(searchState.query?.sortBy || 'score') ===
                                option.value && (
                                <FontAwesomeIcon
                                  icon={faCheck}
                                  className="check-icon"
                                />
                              )}
                            </button>
                          ))}
                        </div>
                      </div>,
                      document.body,
                    )}
                </div>
                <div className="view-dropdown-container">
                  <button
                    ref={viewButtonRef}
                    className="action-btn view-btn"
                    onClick={() => {
                      const next = !viewDropdownOpen;
                      if (next) {
                        setViewMenuPos(
                          computeMenuPosition(viewButtonRef.current),
                        );
                        setSortDropdownOpen(false);
                      }
                      setViewDropdownOpen(next);
                    }}
                    title="Change View"
                    aria-label="Change View"
                    aria-expanded={viewDropdownOpen}
                  >
                    <FontAwesomeIcon icon={faEye} />
                    <span className="btn-text">View</span>
                    <FontAwesomeIcon
                      icon={viewDropdownOpen ? faChevronUp : faChevronDown}
                      className="btn-chevron"
                    />
                  </button>
                  {viewDropdownOpen &&
                    typeof document !== 'undefined' &&
                    createPortal(
                      <div
                        className="view-dropdown-menu view-dropdown-menu--portal"
                        style={{
                          position: 'fixed',
                          top: viewMenuPos?.top ?? 0,
                          right: viewMenuPos?.right ?? 8,
                        }}
                      >
                        {viewOptions.map((option) => (
                          <button
                            key={option.value}
                            className={`view-dropdown-item ${viewType === option.value ? 'active' : ''}`}
                            onClick={() => {
                              setViewType(option.value);
                              setViewDropdownOpen(false);
                            }}
                          >
                            <FontAwesomeIcon icon={option.icon} />
                            <span>{option.label}</span>
                          </button>
                        ))}
                      </div>,
                      document.body,
                    )}
                </div>
              </div>
            </div>
          </div>
          {/* top-section__content */}
        </div>
        <div
          className={`bottom-section ${showFilters ? '' : 'sidebar-hidden'}`}
        >
          {showFilters && (
            <div className="sidebar">
              {showMap && (
                <div className="sidebar-map-container">
                  <div className="map-header">Area of Interest</div>
                  <div className="map-panel">
                    {MapBBoxSelector ? (
                      <MapBBoxSelector />
                    ) : (
                      <div className="map-loading">Loading map...</div>
                    )}
                  </div>
                </div>
              )}
              <div className="sidebar-filters-container">
                <FilterSidebar
                  valueCategoriesCollapsedByDefault={
                    sidebarFacetValueCategoriesCollapsedByDefault
                  }
                />
              </div>
            </div>
          )}
          <div className="main-content-area">
            <div className="metadata-search-block">
              <MetaDataSearchPage
                viewType={viewType}
                detailsPagePath={detailsPagePath}
                cardDisplayFields={cardDisplayFields}
                cardBadgeField={cardBadgeField}
                cardColumns={cardColumns}
                agolEligibleFormats={agolEligibleFormats}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Footer banner - omitted when the page sets none */}
      {footerBannerSrc && (
        <img
          src={footerBannerSrc}
          alt=""
          className="site-catalog-banner site-catalog-banner--footer"
          onError={(e) => {
            e.currentTarget.style.display = 'none';
          }}
        />
      )}
    </div>
  );
};

const MetaDataSearchPageBlockView: React.FC<
  { data?: MetaDataSearchPageBlockData } & Record<string, unknown>
> = (props) => {
  const { data = {} as MetaDataSearchPageBlockData } = props;
  const sidebarDefaultsEnabled = !data._sidebarFacetConfigInitialized;
  const voyagerBaseUrl = data.voyagerBaseUrl || environment.voyager.baseUrl;
  const voyagerConfigId = data.voyagerConfigId || environment.voyager.configId;
  const sidebarFacetFields = parseObjectListValue<VoyagerFacetFieldConfig>(
    data.sidebarFacetFields,
    sidebarDefaultsEnabled ? DEFAULT_VOYAGER_FACET_FIELDS : [],
    (item) => {
      if (!item?.field) {
        return null;
      }

      const categoryPath = parseCategoryPath(item.categoryPath);
      const maxValues = parseOptionalNumber(item.maxValues);
      const valueCategories = parseFacetValueCategories(
        item.valueCategories,
        logError,
      );

      return {
        field: item.field,
        label: item.label || item.field,
        ...(categoryPath ? { categoryPath } : {}),
        ...(item.useShortLabels === true ? { useShortLabels: true } : {}),
        ...(maxValues !== undefined ? { maxValues } : {}),
        ...(valueCategories ? { valueCategories } : {}),
      };
    },
    logError,
  );
  const sidebarFacetParamsList = parseObjectListValue<VoyagerFacetParamConfig>(
    data.sidebarFacetParams,
    sidebarDefaultsEnabled
      ? Object.entries(DEFAULT_VOYAGER_FACET_PARAMS).map(([key, value]) => ({
          key,
          value,
        }))
      : [],
    (item) => {
      if (!item?.key || item?.value === undefined || item?.value === null) {
        return null;
      }

      return {
        key: item.key,
        value: String(item.value),
      };
    },
    logError,
  );
  const sidebarFacetParams = sidebarFacetParamsList.reduce(
    (params, item) => {
      params[item.key] = item.value;
      return params;
    },
    {} as Record<string, string>,
  );
  const sidebarFacetLimit =
    typeof data.sidebarFacetLimit === 'number' ? data.sidebarFacetLimit : 11;
  const sidebarFacetMinCount =
    typeof data.sidebarFacetMinCount === 'number'
      ? data.sidebarFacetMinCount
      : 1;
  const hierarchyTreeBase = data.hierarchyTreeBase || '';

  return (
    <>
      <Helmet
        script={[
          {
            type: 'application/ld+json',
            innerHTML: JSON.stringify(buildAppJsonLd()),
          },
        ]}
      />
      <SearchProvider
        key={hierarchyTreeBase || 'default-hierarchy'}
        baseUrl={voyagerBaseUrl}
        configId={voyagerConfigId}
        sidebarFacetFields={sidebarFacetFields}
        sidebarFacetParams={sidebarFacetParams}
        sidebarFacetLimit={sidebarFacetLimit}
        sidebarFacetMinCount={sidebarFacetMinCount}
        hierarchyTreeBase={hierarchyTreeBase}
        arcgisUrl={data.arcgisUrl}
      >
        <MetaDataSearchPageBlockContent {...props} />
      </SearchProvider>
    </>
  );
};

export default MetaDataSearchPageBlockView;
